import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { format } from "date-fns";
import useLabels from "../../hooks/useLabels";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSelector } from "react-redux";
import { selectConfigs } from "../../context/selectors";
import { getProtocolApplicationStatus } from "../../services/utils";
import { Icon } from "../Icon";
import "./styles/protocol_banner.scss";

const ProtocolApplicationBanner = ({ protocolApplicationId, protocolStepId, protocolSteps = null }) => {
  const [getLabel] = useLabels();
  const axiosPrivate = useAxiosPrivate();
  const configs = useSelector(selectConfigs);
  const dateFormat = configs?.shortDateFormat || "dd/MM/yyyy";

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // If protocolSteps is provided from parent, use it directly (avoid double fetch)
      if (protocolSteps) {
        setSteps(protocolSteps);
        // Still fetch application data for the protocol name
        const data = await getProtocolApplicationStatus(protocolApplicationId, axiosPrivate);
        if (data) {
          setApplication(data.application || null);
        }
      } else {
        // Fallback: fetch both application and steps
        const data = await getProtocolApplicationStatus(protocolApplicationId, axiosPrivate);
        if (data) {
          setApplication(data.application || null);
          setSteps(data.steps || []);
        }
      }
      setLoading(false);
    };
    load();
  }, [protocolApplicationId, protocolSteps, axiosPrivate]);

  if (loading) {
    return (
      <div className="op_protocol_banner">
        <div style={{ color: "gray", padding: "12px 0" }}>
          {getLabel("protocolApplicationLoading")}
        </div>
      </div>
    );
  }

  if (!application) {
    return null;
  }

  // Calculate visible steps
  const currentIdx = steps.findIndex((s) => s.id === protocolStepId);
  const safeCurrentIdx = currentIdx >= 0 ? currentIdx : steps.length - 1;
  const from = Math.max(0, safeCurrentIdx - 2);
  const to = Math.min(steps.length, safeCurrentIdx + 4); // current + 3 next
  const visibleSteps = steps.slice(from, to);

  const appName = application.protocol_name || `Applicazione #${protocolApplicationId}`;

  // Calculate the first next-to-execute step once
  // A step is considered "completed" if:
  // 1. It has executed_at, OR
  // 2. It's ANALYSIS and has been sampled (status != NOT_SAMPLED)
  const isStepCompleted = (step) => {
    if (step.executed_at) return true;
    // ANALYSIS steps that have been sampled (status != NOT_SAMPLED) are considered as done
    if ((step.operation || step.operation_type)?.toUpperCase?.() === "ANALYSIS" && step.validation_status !== "NOT_SAMPLED") return true;
    return false;
  };

  const firstNextToExecuteIndex = steps.findIndex((step, stepIndex) => {
    const prevSteps = steps.slice(0, stepIndex);
    const allPrevCompleted = prevSteps.every(isStepCompleted);
    return allPrevCompleted && !isStepCompleted(step);
  });

  return (
    <div className="op_protocol_banner">
      {/* Header */}
      <div className="op_protocol_banner_header">
        <div className="banner_left">
          <span className="banner_title">{getLabel("operationPartOfProtocol")}</span>
          <span className="banner_name">{appName}</span>
        </div>
        <NavLink to={`/protocol-applications/${protocolApplicationId}`} className="banner_link">
          <Icon icon="fa-arrow-right" /> {getLabel("operationProtocolViewApp")}
        </NavLink>
      </div>

      {/* Mini-timeline */}
      <div className="op_protocol_banner_timeline">
        {/* Indicator: steps before visible range */}
        {from > 0 && (
          <div className="timeline_overflow before">
            {getLabel("operationProtocolMoreBefore", { n: from })}
          </div>
        )}

        {/* Visible steps */}
        {visibleSteps.map((step) => {
          const vStatus = step.validation_status || "PENDING";
          const eStatus = step.execution_status || "PENDING";
          // Show EXECUTED status when step is executed, has executed_at, or when ANALYSIS has been sampled
          const isAnalysisSampled = (step.operation || step.operation_type)?.toUpperCase?.() === "ANALYSIS" && vStatus !== "NOT_SAMPLED";
          const displayStatus = eStatus === "EXECUTED" || !!step.executed_at || isAnalysisSampled ? "EXECUTED" : vStatus;
          const statusIcon = {
            VALID: "✓",
            INVALID: "❌",
            WARNING: "⚠️",
            PENDING: "⏳",
            CONFIGURED: "⚙️",
            EXECUTED: "✓",
          }[displayStatus] || "?";
          const statusLabel = getLabel(`stepStatus${displayStatus}`) || displayStatus;
          const stepDate =
            application.start_date && step.day != null
              ? (() => {
                  const d = new Date(application.start_date);
                  d.setDate(d.getDate() + step.day);
                  return format(d, dateFormat);
                })()
              : null;

          const isCurrentStep = step.id === protocolStepId;

          // This step is next-to-execute only if it's the FIRST step that meets the condition
          const stepIndex = steps.findIndex(s => s.id === step.id);
          const isNextToExecute = stepIndex === firstNextToExecuteIndex;

          // Light green only for VALID steps that are future (not yet completed)
          const isFarFuture = vStatus === "VALID" && !isStepCompleted(step);

          // next_to_execute only for non-ANALYSIS VALID steps (ANALYSIS with collection_date passes it to the next step)
          const isAnalysisStep = (step.operation || step.operation_type)?.toUpperCase?.() === "ANALYSIS";
          const showNextToExecute = isNextToExecute && vStatus === "VALID" && !isAnalysisStep;

          return (
            <div
              key={step.id}
              className={`timeline_item ${vStatus}${isCurrentStep ? " current_step" : ""}${isFarFuture ? " future" : ""}${showNextToExecute ? " next_to_execute" : ""}`}
            >
              <span className="timeline_day">
                {getLabel("protocolApplicationDayLabel", { day: (Number(step.day) || 0) + 1 })}
                {stepDate && (
                  <>
                    <br />
                    <span style={{ fontSize: 11 }}>{stepDate}</span>
                  </>
                )}
              </span>
              <div className="timeline_content">
                <span className="timeline_op">
                  {getLabel(`operationType${step.operation || step.operation_type}`) || "—"}
                </span>
                {step.executed_at && (
                  <span className="timeline_date">
                    {format(new Date(step.executed_at), `${dateFormat} HH:mm`)}
                  </span>
                )}
              </div>
              <span className="timeline_status">
                {statusIcon} {statusLabel}
              </span>
            </div>
          );
        })}

        {/* Indicator: steps after visible range */}
        {to < steps.length && (
          <div className="timeline_overflow after">
            {getLabel("operationProtocolMoreAfter", { n: steps.length - to })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProtocolApplicationBanner;
